import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiPanel,
  EuiTitle,
  EuiSpacer,
  EuiText,
  EuiBadge,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiEmptyPrompt,
  EuiCallOut,
  EuiHorizontalRule,
  EuiToolTip,
  EuiIcon,
  EuiHealth,
  EuiAccordion,
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiSwitch,
  EuiConfirmModal,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { useApi } from "src/api";
import { useAnketaAuth } from "src/auth";
import { AnketaResourcesEnum } from "src/auth";
import { useToast } from "src/notifications";
import {
  PrtgInstanceStatus,
  PrtgSensor,
  PrtgStatusCategory,
} from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import { useOpsanioCan } from "../../can";
import { useChartColors } from "../useChartColors";
import { OpsanioApplianceFlyoutReadiness } from "./readiness";

const CATEGORY_COLOR: Record<PrtgStatusCategory, string> = {
  up: "success",
  down: "danger",
  warning: "warning",
  paused: "subdued",
  unusual: "accent",
  unknown: "subdued",
};

const StatusHealth = ({
  category,
  text,
}: {
  category: PrtgStatusCategory;
  text: string;
}) => {
  const { translate } = useAnketaI18n();
  const categoryLabel: Record<PrtgStatusCategory, string> = {
    up: translate("anketa.opsanio.appliance.flyout.prtg.category.up", {
      defaultMessage: "OK",
    }),
    down: translate("anketa.opsanio.appliance.flyout.prtg.category.down", {
      defaultMessage: "Error",
    }),
    warning: translate(
      "anketa.opsanio.appliance.flyout.prtg.category.warning",
      { defaultMessage: "Warning" },
    ),
    paused: translate("anketa.opsanio.appliance.flyout.prtg.category.paused", {
      defaultMessage: "Paused",
    }),
    unusual: translate(
      "anketa.opsanio.appliance.flyout.prtg.category.unusual",
      { defaultMessage: "Unusual" },
    ),
    unknown: translate(
      "anketa.opsanio.appliance.flyout.prtg.category.unknown",
      { defaultMessage: "Unknown" },
    ),
  };
  return (
    <EuiHealth color={CATEGORY_COLOR[category]} style={{ fontSize: 12 }}>
      {text || categoryLabel[category]}
    </EuiHealth>
  );
};

export const OpsanioApplianceFlyoutPrtg = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();
  // Create group and redeploy: appliance · create AND prtg · create (internal).
  const can = useOpsanioCan();
  const canManagePrtg =
    can(AnketaResourcesEnum.opsanioAppliance, "create") &&
    can(AnketaResourcesEnum.opsanioPrtg, "create", { isInternal: true });
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();

  const [status, setStatus] = useState<PrtgInstanceStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [redeploying, setRedeploying] = useState<boolean>(false);
  const [confirmRedeploy, setConfirmRedeploy] = useState<boolean>(false);
  const [onlyProblems, setOnlyProblems] = useState<boolean>(false);
  const colors = useChartColors();

  const instanceName = useMemo<string>(
    () => appliance.common?.metadata?.name?.trim() ?? "",
    [appliance],
  );

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string",
    [keycloak],
  );


  const getStatus = useCallback(async () => {
    if (!canFetch || instanceName === "") {
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const path = `v2/${AnketaResourcesEnum.opsanio}/prtg/instance/${encodeURIComponent(
        instanceName,
      )}`;
      const { success, data, error } = await Get<{
        status: number;
        data: PrtgInstanceStatus;
      }>({
        path,
        responseType: "json",
        token: keycloak.token,
      });

      if (!success || error) {
        // 503 (not configured) / 502 (PRTG unreachable) all degrade to the
        // same "unavailable" surface - never crash the flyout. Prefer the
        // backend's own error text over the generic axios message.
        setStatus(null);
        setError(
          (error as any)?.service?.debugError ??
            error?.message ??
            translate("anketa.opsanio.appliance.flyout.prtg.error.statusLoad", {
              defaultMessage: "PRTG status could not be loaded.",
            }),
        );
      } else {
        setStatus(data?.data ?? null);
      }
    } catch (err) {
      console.error("Failed to fetch PRTG status:", err);
      setStatus(null);
      setError(
        translate("anketa.opsanio.appliance.flyout.prtg.error.statusLoad", {
          defaultMessage: "PRTG status could not be loaded.",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [canFetch, instanceName, keycloak, translate]);

  useEffect(() => {
    getStatus();
  }, [getStatus]);

  // Provision PRTG monitoring for this instance (clones the opsanio-template
  // group). Used when no PRTG group exists yet; refetches status on success.
  const createGroup = useCallback(async () => {
    if (!canFetch || instanceName === "") return;
    setCreating(true);
    setError(null);
    try {
      const { success, error } = await Post({
        path: `v2/${AnketaResourcesEnum.opsanio}/prtg/instance/${encodeURIComponent(
          instanceName,
        )}`,
        // The backend parses this questionnaire (updateOpsanioCatalog) to derive
        // the per-host devices from server.appliance / server.kubernetes.servers.
        value: { questionnaireId: appliance.id },
        responseType: "json",
        token: keycloak.token,
      });
      if (!success || error) {
        const debugError = (error as any)?.service?.debugError;
        setError(
          debugError ??
            error?.message ??
            translate(
              "anketa.opsanio.appliance.flyout.prtg.error.groupCreate",
              { defaultMessage: "PRTG group could not be created." },
            ),
        );
      } else {
        await getStatus();
      }
    } catch (err) {
      console.error("Failed to create PRTG group:", err);
      setError(
        translate("anketa.opsanio.appliance.flyout.prtg.error.groupCreate", {
          defaultMessage: "PRTG group could not be created.",
        }),
      );
    } finally {
      setCreating(false);
    }
  }, [canFetch, instanceName, keycloak, Post, getStatus, appliance, translate]);

  // Redeploy the instance's PRTG push probes. The backend pauses the probes,
  // deletes the per-node devices, re-provisions them (restoring the push token)
  // and resumes - so the sensors' channels are rebuilt from scratch and pick up
  // the corrected units + alert thresholds on the next readiness.sh push.
  // Destructive: channel history is reset, hence the confirm modal.
  const redeployProbes = useCallback(async () => {
    if (!canFetch || instanceName === "") return;
    setRedeploying(true);
    try {
      const { success, error } = await Post({
        path: `v2/${AnketaResourcesEnum.opsanio}/prtg/instance/${encodeURIComponent(
          instanceName,
        )}/redeploy`,
        // Same source as provision: the backend re-derives the per-host devices
        // from this questionnaire (server.appliance / server.kubernetes.servers).
        value: { questionnaireId: appliance.id },
        responseType: "json",
        token: keycloak.token,
      });
      if (!success || error) {
        const debugError = (error as any)?.service?.debugError;
        addToast({
          title: translate(
            "anketa.opsanio.appliance.flyout.prtg.redeploy.error.title",
            { defaultMessage: "Redeploy failed" },
          ),
          color: "danger",
          text:
            debugError ??
            error?.message ??
            translate(
              "anketa.opsanio.appliance.flyout.prtg.redeploy.error.text",
              { defaultMessage: "The PRTG probes could not be redeployed." },
            ),
        });
      } else {
        addToast({
          title: translate(
            "anketa.opsanio.appliance.flyout.prtg.redeploy.success.title",
            { defaultMessage: "Probes redeployed" },
          ),
          color: "success",
          text: translate(
            "anketa.opsanio.appliance.flyout.prtg.redeploy.success.text",
            {
              values: { instance: instanceName },
              defaultMessage:
                "PRTG probes for {{instance}} were recreated. Channels rebuild on the next readiness push.",
            },
          ),
        });
        await getStatus();
      }
    } catch (err) {
      console.error("Failed to redeploy PRTG probes:", err);
      addToast({
        title: translate(
          "anketa.opsanio.appliance.flyout.prtg.redeploy.error.title",
          { defaultMessage: "Redeploy failed" },
        ),
        color: "danger",
        text: translate(
          "anketa.opsanio.appliance.flyout.prtg.redeploy.error.text",
          { defaultMessage: "The PRTG probes could not be redeployed." },
        ),
      });
    } finally {
      setRedeploying(false);
      setConfirmRedeploy(false);
    }
  }, [
    canFetch,
    instanceName,
    keycloak,
    Post,
    getStatus,
    appliance,
    translate,
    addToast,
  ]);

  // PRTG web UI base for deep links (e.g. ${baseUrl}/device.htm?id=…).
  const baseUrl = useMemo<string>(
    () => (status?.baseUrl ?? "").replace(/\/+$/, ""),
    [status],
  );
  const deviceLink = useCallback(
    (objid: number) =>
      baseUrl ? `${baseUrl}/device.htm?id=${objid}` : undefined,
    [baseUrl],
  );
  const sensorLink = useCallback(
    (objid: number) =>
      baseUrl ? `${baseUrl}/sensor.htm?id=${objid}` : undefined,
    [baseUrl],
  );
  const groupLink = useCallback(
    (id?: number) =>
      baseUrl && id ? `${baseUrl}/group.htm?id=${id}` : undefined,
    [baseUrl],
  );

  const counts = status?.counts;

  // Worst first: down, warning, unusual, then the rest.
  const severity: Record<PrtgStatusCategory, number> = {
    down: 0,
    warning: 1,
    unusual: 2,
    unknown: 3,
    paused: 4,
    up: 5,
  };
  const bySeverity = (a: PrtgSensor, b: PrtgSensor) =>
    severity[a.category] - severity[b.category] ||
    a.sensor.localeCompare(b.sensor);

  // Everything that needs a human, across all devices, in one list.
  const problems = useMemo<PrtgSensor[]>(
    () =>
      (status?.devices ?? [])
        .flatMap((d) => d.sensors)
        .filter((s) => s.category === "down" || s.category === "warning" || s.category === "unusual")
        .sort(bySeverity),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status],
  );

  const categoryLabel: Record<PrtgStatusCategory, string> = {
    up: translate("anketa.opsanio.appliance.flyout.prtg.category.up", {
      defaultMessage: "OK",
    }),
    warning: translate("anketa.opsanio.appliance.flyout.prtg.category.warning", {
      defaultMessage: "Warning",
    }),
    down: translate("anketa.opsanio.appliance.flyout.prtg.category.down", {
      defaultMessage: "Error",
    }),
    unusual: translate("anketa.opsanio.appliance.flyout.prtg.category.unusual", {
      defaultMessage: "Unusual",
    }),
    paused: translate("anketa.opsanio.appliance.flyout.prtg.category.paused", {
      defaultMessage: "Paused",
    }),
    unknown: translate("anketa.opsanio.appliance.flyout.prtg.category.unknown", {
      defaultMessage: "Unknown",
    }),
  };
  const barOrder: PrtgStatusCategory[] = [
    "up",
    "warning",
    "unusual",
    "down",
    "paused",
    "unknown",
  ];
  const barColor: Record<PrtgStatusCategory, string> = {
    up: colors.success,
    warning: colors.warning,
    unusual: colors.accent,
    down: colors.danger,
    paused: colors.subdued,
    unknown: colors.grid,
  };

  const openLink = (href: string | undefined, label: string) =>
    href ? (
      <EuiButtonIcon
        aria-label={label}
        title={label}
        iconType="popout"
        size="xs"
        href={href}
        target="_blank"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      />
    ) : null;

  /** One compact sensor line: status, name (+ message), value, time, link. */
  const sensorRow = (sensor: PrtgSensor, showDevice: boolean) => (
    <EuiFlexGroup
      key={sensor.objid}
      gutterSize="m"
      alignItems="center"
      responsive={false}
      style={{ padding: "6px 0" }}
    >
      <EuiFlexItem grow={false} style={{ width: 110 }}>
        <StatusHealth category={sensor.category} text={categoryLabel[sensor.category]} />
      </EuiFlexItem>
      <EuiFlexItem style={{ minWidth: 0 }}>
        <EuiText size="s">
          <strong>{sensor.sensor}</strong>
        </EuiText>
        {(showDevice || (sensor.message && sensor.category !== "up")) && (
          <EuiText size="xs" color="subdued" style={{ overflowWrap: "anywhere" }}>
            {[showDevice ? sensor.device : "", sensor.category !== "up" ? sensor.message : ""]
              .filter(Boolean)
              .join(" · ")}
          </EuiText>
        )}
      </EuiFlexItem>
      <EuiFlexItem grow={false} style={{ width: 120, textAlign: "right" }}>
        <EuiText size="s">{sensor.lastValue || "-"}</EuiText>
      </EuiFlexItem>
      <EuiFlexItem grow={false} style={{ width: 130, textAlign: "right" }}>
        <EuiText size="xs" color="subdued">
          {sensor.lastCheck || "-"}
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem grow={false} style={{ width: 24 }}>
        {openLink(
          sensorLink(sensor.objid),
          translate("anketa.opsanio.appliance.flyout.prtg.openSensorInPrtg", {
            defaultMessage: "Open sensor in PRTG",
          }),
        )}
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const withDividers = (rows: JSX.Element[]) =>
    rows.map((row, i) => (
      <React.Fragment key={row.key ?? i}>
        {i > 0 && <EuiHorizontalRule margin="none" />}
        {row}
      </React.Fragment>
    ));

  return (
    <EuiFlexGroup direction="column" gutterSize="l">
      {/* ---- Header: title, as-of, actions ---- */}
      <EuiFlexItem>
        <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false} wrap>
          <EuiFlexItem>
            <EuiTitle size="xs">
              <h3>
                {translate("anketa.opsanio.appliance.flyout.prtg.title", {
                  defaultMessage: "PRTG Monitoring",
                })}
              </h3>
            </EuiTitle>
            <EuiText size="xs" color="subdued">
              {status?.group ? (
                <>
                  {status.rootGroup} ▸ {status.group}
                  {status.fetchedAt && (
                    <>
                      {" · "}
                      {translate("anketa.opsanio.appliance.flyout.prtg.fetchedAt", {
                        defaultMessage: "As of:",
                      })}{" "}
                      {new Date(status.fetchedAt).toLocaleString("de-DE", {
                        hour: "2-digit",
                        minute: "2-digit",
                        day: "2-digit",
                        month: "short",
                      })}
                    </>
                  )}
                </>
              ) : (
                translate("anketa.opsanio.appliance.flyout.prtg.subtitle", {
                  defaultMessage: "Live sensor status from PRTG",
                })
              )}
            </EuiText>
          </EuiFlexItem>
          {groupLink(status?.groupId) && (
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                iconType="popout"
                iconSide="right"
                href={groupLink(status?.groupId)}
                target="_blank"
              >
                {translate("anketa.opsanio.appliance.flyout.prtg.openInPrtg", {
                  defaultMessage: "Open in PRTG",
                })}
              </EuiButton>
            </EuiFlexItem>
          )}
          {canManagePrtg && instanceName !== "" && status?.group && (
            <EuiFlexItem grow={false}>
              <EuiToolTip
                content={translate(
                  "anketa.opsanio.appliance.flyout.prtg.redeploy.tooltip",
                  {
                    defaultMessage:
                      "Delete and recreate this instance's push sensors so their channels rebuild with the correct units and alert thresholds. Resets channel history.",
                  },
                )}
              >
                <EuiButtonEmpty
                  onClick={() => setConfirmRedeploy(true)}
                  size="s"
                  color="warning"
                  iconType="package"
                  isLoading={redeploying}
                  isDisabled={redeploying || loading}
                >
                  {translate(
                    "anketa.opsanio.appliance.flyout.prtg.redeploy.button",
                    { defaultMessage: "Redeploy probes" },
                  )}
                </EuiButtonEmpty>
              </EuiToolTip>
            </EuiFlexItem>
          )}
          <EuiFlexItem grow={false}>
            <EuiButtonIcon
              iconType="refresh"
              display="base"
              size="s"
              onClick={getStatus}
              isDisabled={loading || instanceName === ""}
              aria-label={translate("anketa.opsanio.refresh", {
                defaultMessage: "Refresh",
              })}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>

      {instanceName === "" && (
        <EuiFlexItem>
          <EuiCallOut
            title={translate(
              "anketa.opsanio.appliance.flyout.prtg.noInstance.title",
              { defaultMessage: "No instance identifier" },
            )}
            color="warning"
            iconType="help"
          >
            <p>
              {translate(
                "anketa.opsanio.appliance.flyout.prtg.noInstance.body",
                {
                  defaultMessage:
                    "No common.metadata.name is set for this appliance, so no PRTG devices can be assigned.",
                },
              )}
            </p>
          </EuiCallOut>
        </EuiFlexItem>
      )}

      {instanceName !== "" && loading && (
        <EuiFlexItem>
          <EuiFlexGroup
            direction="column"
            alignItems="center"
            gutterSize="m"
            style={{ minHeight: 160, justifyContent: "center" }}
          >
            <EuiLoadingSpinner size="xl" />
            <EuiText size="s" color="subdued">
              {translate("anketa.opsanio.appliance.flyout.prtg.loading", {
                defaultMessage: "Loading PRTG status…",
              })}
            </EuiText>
          </EuiFlexGroup>
        </EuiFlexItem>
      )}

      {instanceName !== "" && !loading && error && (
        <EuiFlexItem>
          <EuiCallOut
            title={translate(
              "anketa.opsanio.appliance.flyout.prtg.unavailable.title",
              { defaultMessage: "PRTG unavailable" },
            )}
            color="danger"
            iconType="alert"
          >
            <p>{error}</p>
            <EuiButton size="s" color="danger" onClick={getStatus}>
              {translate("anketa.common.retry", {
                defaultMessage: "Retry",
              })}
            </EuiButton>
          </EuiCallOut>
        </EuiFlexItem>
      )}

      {instanceName !== "" &&
        !loading &&
        !error &&
        status &&
        status.devices.length === 0 && (
          <EuiFlexItem>
            <EuiEmptyPrompt
              icon={<EuiIcon type="visGauge" size="xl" color="subdued" />}
              title={
                <h3>
                  {translate(
                    "anketa.opsanio.appliance.flyout.prtg.empty.title",
                    { defaultMessage: "No PRTG devices found" },
                  )}
                </h3>
              }
              body={
                <EuiText size="s" color="subdued">
                  {translate(
                    "anketa.opsanio.appliance.flyout.prtg.empty.body",
                    {
                      values: {
                        instance: instanceName,
                        group: status.rootGroup,
                      },
                      defaultMessage:
                        "No monitored devices were found for instance {{instance}} in group {{group}}.",
                    },
                  )}
                  {!status.group && (
                    <>
                      <EuiSpacer size="s" />
                      {translate(
                        "anketa.opsanio.appliance.flyout.prtg.empty.noGroup",
                        {
                          defaultMessage:
                            "No PRTG group exists yet for this instance. It can be created from the opsanio-template template.",
                        },
                      )}
                    </>
                  )}
                </EuiText>
              }
              actions={
                !status.group && canManagePrtg ? (
                  <EuiButton
                    color="primary"
                    fill
                    iconType="plusInCircle"
                    onClick={createGroup}
                    isLoading={creating}
                    disabled={creating}
                  >
                    {translate(
                      "anketa.opsanio.appliance.flyout.prtg.createGroup",
                      { defaultMessage: "Create PRTG group" },
                    )}
                  </EuiButton>
                ) : undefined
              }
            />
          </EuiFlexItem>
        )}

      {instanceName !== "" &&
        !loading &&
        !error &&
        status &&
        counts &&
        status.devices.length > 0 && (
          <>
            {/* ---- Sensor status at a glance ---- */}
            <EuiFlexItem>
              <EuiPanel hasBorder paddingSize="m" hasShadow={false}>
                <EuiFlexGroup gutterSize="m" alignItems="center" responsive={false}>
                  <EuiFlexItem grow={false}>
                    <EuiText size="s">
                      <strong>
                        {translate("anketa.opsanio.appliance.flyout.prtg.sensors.summary", {
                          values: {
                            up: counts.up,
                            total: counts.total,
                            devices: status.devices.length,
                          },
                          defaultMessage:
                            "{{up}} of {{total}} sensors OK · {{devices}} devices",
                        })}
                      </strong>
                    </EuiText>
                  </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer size="s" />
                <div
                  role="img"
                  aria-label={barOrder
                    .filter((c) => counts[c] > 0)
                    .map((c) => `${categoryLabel[c]} ${counts[c]}`)
                    .join(", ")}
                  style={{
                    display: "flex",
                    gap: 2,
                    height: 8,
                    borderRadius: 4,
                    overflow: "hidden",
                    background: colors.grid,
                  }}
                >
                  {barOrder
                    .filter((c) => counts[c] > 0)
                    .map((c) => (
                      <div
                        key={c}
                        style={{ flexGrow: counts[c], background: barColor[c] }}
                      />
                    ))}
                </div>
                <EuiSpacer size="xs" />
                <EuiFlexGroup gutterSize="m" responsive={false} wrap>
                  {barOrder
                    .filter((c) => counts[c] > 0)
                    .map((c) => (
                      <EuiFlexItem grow={false} key={c}>
                        <EuiText size="xs" color="subdued">
                          <span
                            aria-hidden="true"
                            style={{
                              display: "inline-block",
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              marginRight: 6,
                              background: barColor[c],
                            }}
                          />
                          {categoryLabel[c]} {counts[c]}
                        </EuiText>
                      </EuiFlexItem>
                    ))}
                </EuiFlexGroup>
              </EuiPanel>
            </EuiFlexItem>

            {/* ---- The readiness values, grouped by what they measure ---- */}
            <EuiFlexItem>
              <EuiTitle size="xxs">
                <h4>
                  {translate("anketa.opsanio.appliance.flyout.readiness.title", {
                    defaultMessage: "Cluster readiness",
                  })}
                </h4>
              </EuiTitle>
              <EuiText size="xs" color="subdued">
                {translate("anketa.opsanio.appliance.flyout.readiness.subtitle", {
                  defaultMessage:
                    "Values the cluster pushes to PRTG (readiness sensor), grouped by topic. The info icon explains each value and its limits.",
                })}
              </EuiText>
              <EuiSpacer size="s" />
              <OpsanioApplianceFlyoutReadiness instanceName={instanceName} />
            </EuiFlexItem>

            {/* ---- What needs attention, across all devices ---- */}
            <EuiFlexItem>
              <EuiTitle size="xxs">
                <h4>
                  {translate("anketa.opsanio.appliance.flyout.prtg.problems.title", {
                    values: { count: problems.length },
                    defaultMessage: "Needs attention ({{count}})",
                  })}
                </h4>
              </EuiTitle>
              <EuiSpacer size="s" />
              {problems.length === 0 ? (
                <EuiCallOut
                  size="s"
                  color="success"
                  iconType="checkInCircleFilled"
                  title={translate("anketa.opsanio.appliance.flyout.prtg.problems.none", {
                    defaultMessage: "All sensors are OK.",
                  })}
                />
              ) : (
                <EuiPanel hasBorder paddingSize="s" hasShadow={false}>
                  {withDividers(problems.map((sensor) => sensorRow(sensor, true)))}
                </EuiPanel>
              )}
            </EuiFlexItem>

            {/* ---- Devices, as the cloned template lays them out ---- */}
            <EuiFlexItem>
              <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
                <EuiFlexItem>
                  <EuiTitle size="xxs">
                    <h4>
                      {translate("anketa.opsanio.appliance.flyout.prtg.devices.title", {
                        defaultMessage: "Devices",
                      })}
                    </h4>
                  </EuiTitle>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiSwitch
                    compressed
                    label={translate("anketa.opsanio.appliance.flyout.prtg.onlyProblems", {
                      defaultMessage: "Only problems",
                    })}
                    checked={onlyProblems}
                    onChange={(e) => setOnlyProblems(e.target.checked)}
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiSpacer size="s" />
              <EuiPanel hasBorder paddingSize="none" hasShadow={false}>
                {withDividers(
                  status.devices.map((device) => {
                    const deviceProblems =
                      device.counts.down + device.counts.warning + device.counts.unusual;
                    const sensors = [...device.sensors]
                      .filter((s) => !onlyProblems || s.category !== "up")
                      .sort(bySeverity);
                    return (
                      <div key={device.objid} style={{ padding: "4px 12px" }}>
                        <EuiAccordion
                          id={`prtg-device-${device.objid}`}
                          // Only devices with something open start expanded.
                          initialIsOpen={deviceProblems > 0}
                          paddingSize="s"
                          buttonContent={
                            <EuiFlexGroup
                              alignItems="center"
                              gutterSize="s"
                              responsive={false}
                              wrap
                            >
                              <EuiFlexItem grow={false}>
                                <StatusHealth
                                  category={device.category}
                                  text={categoryLabel[device.category]}
                                />
                              </EuiFlexItem>
                              <EuiFlexItem grow={false}>
                                <EuiText size="s">
                                  <strong>{device.device}</strong>
                                </EuiText>
                              </EuiFlexItem>
                              {device.host && (
                                <EuiFlexItem grow={false}>
                                  <EuiText size="xs" color="subdued">
                                    {device.host}
                                  </EuiText>
                                </EuiFlexItem>
                              )}
                              <EuiFlexItem grow={false}>
                                <EuiText size="xs" color="subdued">
                                  {translate(
                                    "anketa.opsanio.appliance.flyout.prtg.device.upOfTotal",
                                    {
                                      values: {
                                        up: device.counts.up,
                                        total: device.counts.total,
                                      },
                                      defaultMessage: "{{up}}/{{total}} OK",
                                    },
                                  )}
                                </EuiText>
                              </EuiFlexItem>
                              {deviceProblems > 0 && (
                                <EuiFlexItem grow={false}>
                                  <EuiBadge color={device.counts.down > 0 ? "danger" : "warning"}>
                                    {deviceProblems}
                                  </EuiBadge>
                                </EuiFlexItem>
                              )}
                            </EuiFlexGroup>
                          }
                          extraAction={openLink(
                            deviceLink(device.objid),
                            translate(
                              "anketa.opsanio.appliance.flyout.prtg.openDeviceInPrtg",
                              { defaultMessage: "Open device in PRTG" },
                            ),
                          )}
                        >
                          {device.message && device.category !== "up" && (
                            <EuiText size="xs" color="subdued">
                              {device.message}
                            </EuiText>
                          )}
                          {sensors.length > 0 ? (
                            withDividers(sensors.map((sensor) => sensorRow(sensor, false)))
                          ) : (
                            <EuiText size="xs" color="subdued">
                              <em>
                                {onlyProblems
                                  ? translate(
                                      "anketa.opsanio.appliance.flyout.prtg.device.allOk",
                                      { defaultMessage: "All sensors of this device are OK." },
                                    )
                                  : translate(
                                      "anketa.opsanio.appliance.flyout.prtg.device.noSensors",
                                      { defaultMessage: "No sensors for this device." },
                                    )}
                              </em>
                            </EuiText>
                          )}
                        </EuiAccordion>
                      </div>
                    );
                  }),
                )}
              </EuiPanel>
            </EuiFlexItem>
          </>
        )}

      {confirmRedeploy && (
        <EuiConfirmModal
          title={translate(
            "anketa.opsanio.appliance.flyout.prtg.redeploy.confirm.title",
            { defaultMessage: "Redeploy PRTG probes?" },
          )}
          onCancel={() => setConfirmRedeploy(false)}
          onConfirm={redeployProbes}
          cancelButtonText={translate("anketa.create.action.cancel", {
            defaultMessage: "Cancel",
          })}
          confirmButtonText={translate(
            "anketa.opsanio.appliance.flyout.prtg.redeploy.confirm.button",
            { defaultMessage: "Redeploy" },
          )}
          buttonColor="warning"
          defaultFocusedButton="cancel"
          isLoading={redeploying}
        >
          <p>
            {translate(
              "anketa.opsanio.appliance.flyout.prtg.redeploy.confirm.body",
              {
                values: { instance: instanceName },
                defaultMessage:
                  "This deletes and recreates the push sensors for {{instance}} so their channels are rebuilt with the correct units and alert thresholds. Channel history is reset and monitoring is briefly paused during the redeploy. Continue?",
              },
            )}
          </p>
        </EuiConfirmModal>
      )}
    </EuiFlexGroup>
  );
};
